import IMask from "imask";
import React, {
  Children,
  cloneElement,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Input } from "./input";
import { InputProps, MaskedInputProps, MaskProps } from "./input.types";

/**
 * Input component to accept masked input.
 *
 * It uses the [iMask](https://imask.js.org/) library.
 *
 * Adding this component to an App or MFE will result in the iMask library getting added to the app JS bundle.
 * (~125 KB / ~54 KB Minified / ~14 KB minified + gzipped )
 */
export const MaskedInput = ({
  mask,
  maskOptions: maskOptionsProp,
  onComplete,
  unmask,
  onChange,
  ...inputProps
}: MaskedInputProps) => {
  const maskOptions = useMemo(
    () => (maskOptionsProp ? maskOptionsProp : mask ? { mask } : undefined),
    [mask, maskOptionsProp]
  );

  const inputElementRenderer = useCallback<InputProps["inputElementRenderer"]>(
    ({ value, onChange, ref, ...inputElementProps }) =>
      maskOptions ? (
        <Mask
          maskOptions={maskOptions}
          unmask={unmask}
          value={value ? value : ""}
          onAccept={onChange}
          onComplete={onComplete}
          ref={ref}
        >
          <input {...inputElementProps} />
        </Mask>
      ) : (
        <input value={value} onChange={onChange} ref={ref} {...inputElementProps} />
      ),
    [maskOptions, onComplete, unmask]
  );

  return (
    <Input {...inputProps} onChange={onChange as InputProps["onChange"]} inputElementRenderer={inputElementRenderer} />
  );
};

/**
 * A React component wrapper for the iMask library.
 */
const Mask: ForwardRefExoticComponent<MaskProps & RefAttributes<HTMLInputElement>> = forwardRef(
  ({ unmask = false, maskOptions, value, onAccept, onComplete, children }, ref) => {
    const inputElementRef = useRef<HTMLInputElement>(null);
    const maskValue = useRef<any>(null);
    const maskRef = useRef<any>();

    const getMaskValue = useCallback(() => {
      if (unmask === "typed") {
        return maskRef.current.typedValue;
      }
      if (unmask) {
        return maskRef.current.unmaskedValue;
      }
      return maskRef.current.value;
    }, [unmask]);

    const setMaskValue = useCallback(
      (value) => {
        value = value === null || value === undefined ? "" : value;
        if (unmask === "typed") {
          maskRef.current.typedValue = value;
        } else if (unmask) {
          maskRef.current.unmaskedValue = `${value}`;
        } else {
          maskRef.current.value = value;
        }
      },
      [unmask]
    );

    const handleAccept = useCallback(
      (e) => {
        if (onAccept) {
          const value = getMaskValue();
          onAccept(e, value);
        }
      },
      [getMaskValue, onAccept]
    );

    const handleComplete = useCallback(
      (e) => {
        if (onComplete) {
          const value = getMaskValue();
          onComplete(e, value);
        }
      },
      [getMaskValue, onComplete]
    );

    const initMask = useCallback(
      (ref) => {
        maskRef.current = IMask(ref.current, maskOptions).on("accept", handleAccept).on("complete", handleComplete);
        maskValue.current = ref.current.value;
      },
      [maskOptions, handleAccept, handleComplete]
    );

    const destroyMask = useCallback(() => {
      if (maskRef.current) {
        maskRef.current.destroy();
        maskRef.current = null;
      }
    }, []);

    useEffect(() => {
      if (!maskRef.current) {
        initMask(ref ? ref : inputElementRef);
        return () => destroyMask();
      }
    }, [destroyMask, initMask, ref]);

    useEffect(() => {
      if (maskRef.current) {
        if (value === getMaskValue()) {
          maskRef.current.updateOptions(maskOptions);
          setMaskValue(value);
        }
      }
    }, [getMaskValue, maskOptions, setMaskValue, value]);

    useEffect(() => {
      if (maskRef.current) {
        if (value !== getMaskValue()) {
          setMaskValue(value);
        }
      }
    }, [getMaskValue, setMaskValue, value]);

    return (
      children &&
      Children.only(children) &&
      cloneElement(children, {
        ref: ref ? ref : inputElementRef,
      })
    );
  }
);

Mask.displayName = "Mask";
